import React from "react";
import { ExternalLink } from "lucide-react";

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

/**
 * Format an URL as a friendly inline label:
 *   https://www.instagram.com/p/abc/?foo=bar  -->  instagram.com/p/abc
 * Drops the scheme, the leading "www." and any trailing query / fragment.
 * Truncates the path for very long URLs.
 */
function prettyLabel(href) {
    try {
        const u = new URL(href);
        const host = u.hostname.replace(/^www\./, "");
        let path = u.pathname.replace(/\/+$/, "");
        if (path.length > 32) path = path.slice(0, 29) + "...";
        return host + path;
    } catch {
        return href.replace(/^https?:\/\//, "").replace(/^www\./, "");
    }
}

/**
 * RichText renders plain text and turns URLs into pill-shaped previews
 * showing only the host + truncated path (no `https://`). Click opens
 * the destination in a new tab.
 *
 * Usage: <RichText text={event.description} />
 */
export default function RichText({ text, className = "" }) {
    if (!text || typeof text !== "string") return null;
    const parts = [];
    let last = 0;
    let match;
    let key = 0;
    URL_REGEX.lastIndex = 0;
    while ((match = URL_REGEX.exec(text)) !== null) {
        if (match.index > last) {
            parts.push(text.slice(last, match.index));
        }
        const href = match[0];
        parts.push(
            <a key={`u-${key++}-${match.index}`}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid="rich-link"
                className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[12px] font-semibold border border-primary/20 align-middle">
                <span className="truncate max-w-[18ch]">{prettyLabel(href)}</span>
                <ExternalLink size={11} className="flex-shrink-0" />
            </a>
        );
        last = match.index + href.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return (
        <span className={`whitespace-pre-line break-words ${className}`}>
            {parts.map((p, i) => typeof p === "string"
                ? <React.Fragment key={`t-${i}`}>{p}</React.Fragment>
                : p)}
        </span>
    );
}
